import { createContext, useContext, useCallback, useState, type ReactNode } from 'react';
import { AUTO_FIL } from './auto.fil';
import { OVERRIDES_FIL } from './overrides.fil';

// ─────────────────────────────────────────────────────────────────────────────
// App-wide language switch (English ⇄ Filipino).
//
// SINGLE SOURCE OF TRUTH = ENGLISH. You only ever write/edit English in the UI.
// Filipino is generated automatically by `npm run i18n` (uses Gemini) into
// `auto.fil.ts`, keyed by the exact English text. So changing an English label
// changes BOTH languages: English updates immediately, and the next `npm run
// i18n` re-translates that label for Filipino. You never hand-edit Filipino —
// except optional corrections in `overrides.fil.ts`, which always win.
//
// Resolution order for Filipino:  overrides → generated cache → English.
// (If a label has no Filipino yet, it simply shows English — never blank.)
//
// Scope: ON-SCREEN UI ONLY. Generated business documents (proposals, BOM, RFI,
// submittals, exports in `lib/*`) are ALWAYS English by design and never use
// this. Trade/technical terms stay English in both languages (the generator is
// told to keep them English). Currency/number formats stay US.
//
// Usage in any component:
//   const { t } = useLang();
//   <h1>{t('New Bid')}</h1>                       // preferred: English only
//   <h1>{t({ en: 'New Bid', fil: 'Bagong Bid' })}</h1>   // also supported (legacy)
// In the object form the `fil` value is ignored at render time — the generated
// cache is authoritative — but it's a harmless hint and a fallback seed.
// ─────────────────────────────────────────────────────────────────────────────

// ── Supported languages (data-driven) ───────────────────────────────────────
// English is the source of truth; every other entry is a translation layer.
//
// ▶ TO ADD A LANGUAGE (e.g. Cebuano 'ceb'):
//   1. Add it to TARGETS in scripts/i18n-translate.mjs, then run `npm run i18n`
//      (generates i18n/auto.ceb.ts; create an empty i18n/overrides.ceb.ts too).
//   2. Add one line to LANGUAGES below, and one line to DICTIONARIES below
//      (import its AUTO_/OVERRIDES_).
//   That's all — every untranslated string automatically falls back to English,
//   and NO component or t(...) call site needs to change.
export const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'fil', label: 'Filipino', short: 'FIL' },
  // { code: 'ceb', label: 'Cebuano', short: 'CEB' },
] as const;
export type Lang = (typeof LANGUAGES)[number]['code'];
const LANG_CODES = LANGUAGES.map((l) => l.code) as readonly string[];
const DEFAULT_LANG: Lang = 'en';

type Dict = { auto: Record<string, string>; overrides: Record<string, string> };
const DICTIONARIES: Partial<Record<Lang, Dict>> = {
  fil: { auto: AUTO_FIL, overrides: OVERRIDES_FIL },
  // ceb: { auto: AUTO_CEB, overrides: OVERRIDES_CEB },
};

/** A piece of UI text. Either a plain English string (preferred) or a legacy
 *  {en, fil} pair. In both cases the ENGLISH text is the lookup key; the inline
 *  `fil` is just a hint/seed and is ignored at render (the dictionaries win). */
export interface Bilingual {
  en: string;
  fil: string;
}
export type Text = string | Bilingual;

/** The English text of any Text value — this is the translation key. */
export function englishOf(x: Text): string {
  return typeof x === 'string' ? x : x.en;
}

interface LangState {
  lang: Lang;
  setLang: (l: Lang) => void;
  /** Resolve a piece of UI text for the current language. */
  t: (x: Text) => string;
}

const Ctx = createContext<LangState | null>(null);
const STORAGE_KEY = 'aisolution.lang';

function initialLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && LANG_CODES.includes(v)) return v as Lang;
  } catch {
    /* localStorage unavailable (private mode, etc.) — default below. */
  }
  return DEFAULT_LANG;
}

/** Resolve an English key for a language: manual override → generated cache →
 *  English. Any language not in DICTIONARIES (or any untranslated key) shows
 *  English, so a partially-translated language is always safe. */
function translate(lang: Lang, en: string): string {
  if (lang === DEFAULT_LANG) return en;
  const d = DICTIONARIES[lang];
  return d?.overrides[en] ?? d?.auto[en] ?? en;
}

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore persistence failures */
    }
  }, []);

  const t = useCallback((x: Text) => translate(lang, englishOf(x)), [lang]);

  return <Ctx.Provider value={{ lang, setLang, t }}>{children}</Ctx.Provider>;
}

export function useLang(): LangState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useLang must be used within LangProvider');
  return v;
}
